"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { TrendingUp, Search, MapPin, ChartBar as BarChart3, CircleCheck as CheckCircle2, ArrowRight, Compass, Target, Zap, Building2, ShieldCheck, ExternalLink } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const wakefieldMetrics = [{
  id: 1,
  title: "Local Search Growth",
  value: "+38%",
  change: "YoY increase",
  description: "Year-on-year demand for commercial services in Wakefield."
}, {
  id: 2,
  title: "Mobile Intent Share",
  value: "67%",
  change: "Near-me searches",
  description: "Local queries performed on mobile devices within WF postcodes."
}, {
  id: 3,
  title: "Map Pack Conversions",
  value: "4.2x",
  change: "Higher lead rate",
  description: "Top 3 Google Business Profile rankings vs organic standard listings."
}, {
  id: 4,
  title: "Unclaimed Visibility",
  value: "54%",
  change: "Competitor gap",
  description: "Wakefield competitors lacking optimized structured local schema."
}];
const wakefieldKeywords = [{
  id: 1,
  term: "SEO consultant Wakefield",
  volume: "High Intent",
  intent: "Commercial",
  difficulty: "Medium",
  opportunity: "Immediate top 3 potential with dedicated geo-signals."
}, {
  id: 2,
  term: "B2B services Wakefield",
  volume: "Moderate",
  intent: "Transactional",
  difficulty: "Low-Medium",
  opportunity: "Strong candidate for targeted landing page architecture."
}, {
  id: 3,
  term: "Digital marketing consultancy WF1",
  volume: "Niche",
  intent: "High Value",
  difficulty: "Low",
  opportunity: "Fast capture of local industrial and professional service leads."
}, {
  id: 4,
  term: "Local SEO strategy West Yorkshire",
  volume: "High Growth",
  intent: "Informational",
  difficulty: "Medium",
  opportunity: "Ideal for cluster content and regional authority building."
}];
const competitorInsights = [{
  id: 1,
  factor: "Generic Regional Pages",
  agencyApproach: "Duplicated copy across 20 Yorkshire town subpages.",
  myApproach: "Hand-crafted Wakefield landing page with genuine local entity schema."
}, {
  id: 2,
  factor: "Google Business Profile",
  agencyApproach: "Basic setup with secondary categories ignored.",
  myApproach: "Granular attribute optimization, weekly posts, local citation audit."
}, {
  id: 3,
  factor: "Technical Local Schema",
  agencyApproach: "Standard Organization JSON-LD snippet only.",
  myApproach: "Full GeoCoordinates, LocalBusiness, and ServiceArea RDFa markup."
}];
const strategicOpportunities = [{
  id: 1,
  title: "WF Postcode Radius Targeting",
  tag: "High Impact",
  desc: "Capturing searchers from WF1 through WF9 by aligning content with specific industrial estates and commercial hubs in Wakefield."
}, {
  id: 2,
  title: "Local Citation Clean-up",
  tag: "Authority",
  desc: "Eliminating duplicate and inaccurate NAP data across UK directories to build Google Trust rank faster."
}, {
  id: 3,
  title: "Geo-Targeted Content Hubs",
  tag: "Traffic Boost",
  desc: "Building contextually rich articles focused on Wakefield business growth, B2B supplier needs, and regional case studies."
}, {
  id: 4,
  title: "Review Velocity Management",
  tag: "Conversion Rate",
  desc: "Establishing structured client review workflows to dominate the local 3-pack social proof signal."
}];
const neighboringCities = [{
  id: 1,
  name: "Leeds",
  distance: "10 miles north",
  focus: "Financial & Tech SEO Hub",
  path: "/LocalSEOPage"
}, {
  id: 2,
  name: "Bradford",
  distance: "16 miles northwest",
  focus: "E-commerce & Manufacturing",
  path: "/LocalSEOPage"
}, {
  id: 3,
  name: "Huddersfield",
  distance: "14 miles west",
  focus: "Industrial & B2B Lead Gen",
  path: "/LocalSEOPage"
}, {
  id: 4,
  name: "Barnsley",
  distance: "11 miles south",
  focus: "Logistics & Commercial SEO",
  path: "/LocalSEOPage"
}, {
  id: 5,
  name: "Sheffield",
  distance: "24 miles south",
  focus: "Enterprise & Regional SEO",
  path: "/LocalSEOPage"
}, {
  id: 6,
  name: "York",
  distance: "28 miles northeast",
  focus: "Tourism & Professional Services",
  path: "/LocalSEOPage"
}];
export default function Wakefield() {
  const shouldReduceMotion = useReducedMotion();
  const [activeTab, setActiveTab] = useState("keywords");
  return <section id="wakefieldlocalseoanalysis" data-nav="light" className="bg-background text-foreground py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto transition-colors duration-300">
      <div className="text-center max-w-3xl mx-auto mb-16">
        <Badge variant="secondary" className="mb-4 bg-secondary text-secondary-foreground font-medium px-3 py-1">
          <MapPin className="size-3.5 text-accent me-1" />
          West Yorkshire Search Intelligence
        </Badge>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-foreground font-serif mb-6">
          Wakefield Local SEO Analysis &amp; Opportunities
        </h2>
        <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
          I closely track local search behavior across Wakefield and the broader West Yorkshire corridor. Here is my breakdown of search trends, high-intent keywords, and actionable opportunities for local businesses seeking sustainable growth.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
        {wakefieldMetrics.map((item, i) => <motion.div key={item.id} data-index={i} initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.4,
        delay: i * 0.1
      }}>
            <Card className="border border-border/80 bg-card hover:border-primary/40 transition-[border-color,box-shadow] duration-200">
              <CardHeader className="pb-2">
                <CardDescription className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                  {item.title}
                </CardDescription>
                <CardTitle className="text-3xl font-bold text-primary font-serif">
                  {item.value}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-1.5 text-xs text-accent font-medium mb-1">
                  <TrendingUp className="size-3.5" />
                  <span>{item.change}</span>
                </div>
                <p className="text-xs text-muted-foreground leading-normal">
                  {item.description}
                </p>
              </CardContent>
            </Card>
          </motion.div>)}
      </div>

      <div className="bg-muted/50 border border-border rounded-xl p-6 mb-16">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-8">
          <div>
            <h3 className="text-xl sm:text-2xl font-semibold font-serif text-foreground">
              Local Market Diagnostics
            </h3>
            <p className="text-sm text-muted-foreground">
              Select a view to inspect specific keywords or direct consultant strategic advantages.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 w-full md:w-auto">
            <Button variant={activeTab === "keywords" ? "default" : "outline"} onClick={() => setActiveTab("keywords")} className="text-xs sm:text-sm font-medium">
              <Search className="size-4 me-1.5" />
              Wakefield Keywords
            </Button>
            <Button variant={activeTab === "competition" ? "default" : "outline"} onClick={() => setActiveTab("competition")} className="text-xs sm:text-sm font-medium">
              <BarChart3 className="size-4 me-1.5" />
              Consultant Advantage
            </Button>
          </div>
        </div>

        {activeTab === "keywords" && <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.3
      }} className="overflow-x-auto">
            <div className="min-w-[600px]">
              <div className="grid grid-cols-12 gap-4 pb-3 border-b border-border text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                <div className="col-span-4">Target Keyword</div>
                <div className="col-span-2">Search Volume</div>
                <div className="col-span-2">Search Intent</div>
                <div className="col-span-4">Strategic Value</div>
              </div>
              {wakefieldKeywords.map((kw, i) => <div key={kw.id} data-index={i} className="grid grid-cols-12 gap-4 py-4 border-b border-border/50 items-center text-sm hover:bg-background/60 transition-colors px-2 rounded-md">
                  <div className="col-span-4 font-medium text-foreground flex items-center gap-2">
                    <Search className="size-4 text-primary shrink-0" />
                    <span>{kw.term}</span>
                  </div>
                  <div className="col-span-2">
                    <Badge variant="outline" className="text-xs font-normal">
                      {kw.volume}
                    </Badge>
                  </div>
                  <div className="col-span-2 text-xs text-muted-foreground font-medium">
                    {kw.intent}
                  </div>
                  <div className="col-span-4 text-xs text-foreground/90 leading-normal">
                    {kw.opportunity}
                  </div>
                </div>)}
            </div>
          </motion.div>}

        {activeTab === "competition" && <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.3
      }} className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {competitorInsights.map((insight, i) => <Card key={insight.id} data-index={i} className="bg-background border-border">
                <CardHeader>
                  <CardTitle className="text-lg font-serif font-semibold text-foreground">
                    {insight.factor}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-xs">
                  <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-foreground">
                    <span className="font-semibold text-destructive block mb-1 uppercase tracking-wide text-[11px]">
                      Traditional Agency
                    </span>
                    {insight.agencyApproach}
                  </div>
                  <div className="p-3 rounded-lg bg-primary/10 border border-primary/20 text-foreground">
                    <span className="font-semibold text-primary block mb-1 uppercase tracking-wide text-[11px]">
                      My Hands-On Process
                    </span>
                    {insight.myApproach}
                  </div>
                </CardContent>
              </Card>)}
          </motion.div>}
      </div>

      <div className="mb-20">
        <div className="flex flex-col md:flex-row items-start md:items-end justify-between mb-10 gap-4">
          <div>
            <Badge variant="outline" className="mb-2 border-primary/30 text-primary">
              <Zap className="size-3 me-1" /> Key Action Areas
            </Badge>
            <h3 className="text-2xl sm:text-3xl font-serif font-semibold text-foreground">
              Wakefield Growth Opportunities
            </h3>
          </div>
          <p className="text-sm text-muted-foreground max-w-md">
            When you work directly with me, we execute these high-leverage local SEO tactics specifically tailored to Wakefield businesses.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {strategicOpportunities.map((opp, i) => <motion.div key={opp.id} data-index={i} initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.3,
          delay: i * 0.1
        }}>
              <Card className="h-full border border-border bg-card hover:border-primary/40 transition-[border-color] duration-200">
                <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-3">
                  <CardTitle className="text-lg font-semibold text-foreground font-serif">
                    {opp.title}
                  </CardTitle>
                  <Badge variant="secondary" className="text-[12px] font-medium bg-secondary text-secondary-foreground">
                    {opp.tag}
                  </Badge>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {opp.desc}
                  </p>
                </CardContent>
              </Card>
            </motion.div>)}
        </div>
      </div>

      <div className="mb-16 bg-card border border-border rounded-xl p-8">
        <div className="text-center max-w-2xl mx-auto mb-8">
          <Badge variant="secondary" className="mb-2 bg-secondary text-secondary-foreground">
            <Compass className="size-3.5 me-1 text-accent" /> Regional SEO Expansion
          </Badge>
          <h3 className="text-xl sm:text-2xl font-serif font-semibold text-foreground mb-2">
            Neighboring West &amp; South Yorkshire Hubs
          </h3>
          <p className="text-sm text-muted-foreground">
            Local search performance in Wakefield connects directly with neighboring commercial centers. Explore SEO strategies across surrounding regional hubs.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {neighboringCities.map((city, i) => <div key={city.id} data-index={i} className="p-4 rounded-lg border border-border/70 bg-background hover:border-primary/50 transition-[border-color,box-shadow] duration-200 flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold text-foreground font-serif text-base">
                    {city.name}
                  </span>
                  <span className="text-[12px] text-muted-foreground font-medium bg-muted px-2 py-0.5 rounded">
                    {city.distance}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mb-3">
                  {city.focus}
                </p>
              </div>
              <Link to={city.path} className="inline-flex items-center text-xs font-semibold text-primary hover:text-primary/80 transition-colors pt-2 border-t border-border/40">
                <span>Explore {city.name} SEO</span>
                <ArrowRight className="size-3 ms-1" />
              </Link>
            </div>)}
        </div>
      </div>

      <div className="bg-primary text-primary-foreground rounded-2xl p-8 sm:p-12 text-center relative overflow-hidden shadow-lg">
        <div className="max-w-2xl mx-auto relative z-10">
          <div className="inline-flex items-center gap-2 bg-primary-foreground/10 px-3 py-1 rounded-full text-xs font-medium mb-6">
            <ShieldCheck className="size-4 text-accent" />
            <span>Direct Consultant Access &bull; No Account Managers</span>
          </div>
          <h3 className="text-xl sm:text-2xl font-serif font-bold text-primary-foreground mb-4">
            Ready to Outrank Competitors in Wakefield?
          </h3>
          <p className="text-primary-foreground/90 text-sm sm:text-base mb-8 leading-relaxed">
            I work directly with ambitious businesses across Wakefield and West Yorkshire to deliver measurable organic traffic and verified leads. Let's review your site together.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button asChild size="lg" className="bg-accent hover:bg-accent/90 text-accent-foreground font-medium shadow-md w-full sm:w-auto">
              <Link to="/ContactPage">
                <span>Book a Free SEO Consultation</span>
                <ArrowRight className="size-4 ms-2" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/20 w-full sm:w-auto">
              <Link to="/CaseStudiesPage">
                <span>Explore My Case Studies</span>
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}